import {
    makeStyles 
} from '@mui/styles';
import {
    Box, Slide, 
    Typography,
    useTheme
} from '@mui/material';
import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import ProgressBar from '../components/progressBar';
import {
    useContext, useEffect, useRef, useState 
} from 'react';
import {
    DiagnosticFormContext 
} from '../../../contexts/form';
import {
    TypographyWeights 
} from '../../../figmaTheme';
import SunnysideButton, {
    ButtonSizes, ButtonVariants 
} from '../../../components_sunnyside/button';

type Props = {
    position: number,
    sectionKey: string,
    backJump?: number
};

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;
const skippyEnvironment = new Set(['development', 'staging']);

const useStyles = makeStyles((theme) => ({
    scrollableContainer: {
        overflow: 'scroll',
        scrollbarWidth: 'none',
        minHeight: '100%',
        width: '100vw',
        maxWidth: '692px',
        transform: 'translate(-24px, 0)',
        padding: '0 24px'
    },
    textContainer: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '12px'
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '12px'
    },
    title: {
        fontFamily: 'Poppins',
        fontSize: '20px',
        lineHeight: '28px',
        color: '#333',
        margin: '0'
    },
    subtitle: {
        fontFamily: 'Poppins',
        fontSize: '14px',
        lineHeight: '20px',
        color: '#333',
        margin: '0',
        marginBottom: '30px'
    },
    inputContainer: {
        opacity: 0,
        animation: '$fadeIn 0.5s forwards 0.5s',
    },
    label: {
        fontFamily: 'Poppins',
        fontSize: '14px',
        lineHeight: '24px',
        fontWeight: 600,
        color: '#333',
        margin: '0 0 4px'
    },
    input: {
        fontSize: '16px',
        padding: '12px 16px',
        borderRadius: '12px',
        border: '1px solid #EBEBEB',
        outline: 'none',
        width: '100%',
        ['&:invalid']: {
            border: '1px solid red'
        }
    },
    skippy: {
        backgroundColor: theme.palette.error.main,
        margin: '0 auto'
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: '0' 
        },
        '100%': {
            opacity: '1' 
        },
    }
}));

export const NameInputScreen = ({
    position,
    sectionKey,
    backJump
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const classes = useStyles();
    const theme = useTheme();
    const [value, setValue] = useState(formContext.answers.your_name || '');
    const [showButton, setShowButton] = useState(value.length > 0);
    const inputBox = useRef(null);

    useEffect(() => {
        if (!inputBox.current) return;

        inputBox.current.focus();
    }, [inputBox.current]);

    const onClick = () => {
        formContext.setAnswers({
            your_name: value 
        });

        if (inputBox.current) inputBox.current.blur();

        setTimeout(() => {
            formContext.goNext();
        }, 50);
    };

    const onChange = (e) => {
        setValue(e.target.value);
        if (e.target.value && e.target?.checkValidity()) {
            setShowButton(true);
        } else {
            setShowButton(false);
        }
    };

    const onKeyPress = (e) => {
        if (!value) return;

        if (e.key === 'Enter') onClick();
    };

    return (
        <CardContainer
            sectionKey={sectionKey}
            backJump={backJump}
            goBack={formContext.goBack}
            goNext={showButton && onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={sectionKey}
                />
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Slide
                            in={true}
                            direction={formContext.screenDirection}
                            timeout={500}
                            mountOnEnter
                            unmountOnExit
                        >
                            <Box className={classes.textContainer}>
                                <p
                                    className={classes.title}
                                    dangerouslySetInnerHTML={{
                                        __html: 'Before we jump in, let\'s pause for introductions. What should we call you?' 
                                    }}
                                />
                                <p
                                    className={classes.subtitle}
                                    dangerouslySetInnerHTML={{
                                        __html: 'Your first name is all we need, or a nickname works too if you\'d prefer to stay anonymous' 
                                    }}
                                />
                            </Box>
                        </Slide>
                        <Box className={classes.inputContainer}>
                            <p className={classes.label}>
                                {'Your first name (or nickname)'}
                            </p>
                            <input
                                ref={inputBox}
                                autoCorrect={'off'}
                                spellCheck={false}
                                autoFocus
                                autoComplete={'given-name'}
                                name={'name'}
                                type={'text'}
                                aria-invalid={'false'}
                                className={classes.input}
                                placeholder={'Enter your name'}
                                onChange={onChange}
                                onKeyPress={onKeyPress}
                                value={value}
                            />
                        </Box>
                        {showButton && (skippyEnvironment.has(ENVIRONMENT)) && formContext.skippySkipsName && (
                            <SunnysideButton
                                buttonVariant={ButtonVariants.PRIMARY}
                                buttonSize={ButtonSizes.LARGE}
                                onClick={formContext.skippySkipsName}
                                className={classes.skippy}
                            >
                                <Typography
                                    style={{
                                        fontWeight: TypographyWeights.BOLD,
                                        color: theme.palette.white
                                    }}
                                >
                                    SKIPPY
                                </Typography>
                            </SunnysideButton>
                        )}
                    </Box>
                    <Box
                        style={{
                            height: '300px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default NameInputScreen;
